import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import { VStepperVertical } from 'vuetify/components/VStepperVertical'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { VFileUpload } from 'vuetify/components/VFileUpload'
import { VVideo } from 'vuetify/labs/VVideo'
import { createRulesPlugin } from 'vuetify'
import mdiCalendarBlank from '~icons/mdi/calendar-blank'
import i18n from './i18n'
import { useI18n } from 'vue-i18n'
// TOOO ? import DateFnsAdapter from '@date-io/date-fns'

const vuetifyLocaleKeyMap: Record<string, string> = {
  // Data table & iterator
  '$vuetify.dataIterator.noResultsText': 'common.no_match',
  '$vuetify.dataIterator.loadingText': 'common.loading',
  '$vuetify.dataFooter.itemsPerPageText': 'pagination.items_per_page',
  '$vuetify.dataFooter.itemsPerPageAll': 'common.all',
  '$vuetify.dataFooter.nextPage': 'common.next',
  '$vuetify.dataFooter.pageText': 'pagination.range',
  // General
  '$vuetify.noDataText': 'common.no_data_available',
  '$vuetify.loading': 'common.loading',
  '$vuetify.open': 'common.open',
  '$vuetify.close': 'common.close',
  '$vuetify.dismiss': 'common.close',
  // Confirm edit dialog
  '$vuetify.confirmEdit.ok': 'common.ok',
  '$vuetify.confirmEdit.cancel': 'common.cancel',
  // Stepper
  '$vuetify.stepper.next': 'common.next',
  // Pagination aria labels
  '$vuetify.pagination.ariaLabel.next': 'common.next',
  // File upload
  '$vuetify.fileUpload.title': 'common.select_file',
  // Rules
  '$vuetify.rules.required': 'common.required',
  '$vuetify.rules.email': 'common.email_address_must_be_valid',
  '$vuetify.rules.number': 'regex.invalid_number',
  '$vuetify.rules.integer': 'rules.integer',
  // '$vuetify.rules.capital': '',
  '$vuetify.rules.maxLength': 'rules.maxlength',
  '$vuetify.rules.minLength': 'rules.minlength',
  '$vuetify.rules.strictLength': 'rules.strict_length',
  // '$vuetify.rules.exclude': '',
  // '$vuetify.rules.notEmpty': '',
  // '$vuetify.rules.pattern': '',
  // Can't find translations for the commented rules, maybe we should add them
  // Date picker
  '$vuetify.datePicker.title': 'common.select_date',
  '$vuetify.datePicker.header': 'common.enter_date',
}

/**
 * Creates a locale adapter that wraps the vue-i18n adapter and maps
 * Vuetify's internal locale keys to our application's keys.
 */
function createMappedVueI18nAdapter() {
  const baseAdapter = createVueI18nAdapter({ i18n, useI18n })

  return {
    ...baseAdapter,
    t: (key: string, ...params: unknown[]) => {
      // Check if this Vuetify key has a mapping to our app's keys
      const mappedKey = vuetifyLocaleKeyMap[key]
      if (mappedKey) {
        // Use our app's key instead, handle params properly
        if (params.length > 0) {
          return i18n.global.t(mappedKey, params)
        }
        return i18n.global.t(mappedKey)
      }
      // Fall back to the base adapter for unmapped keys
      return baseAdapter.t(key, ...params)
    },
  }
}

const inputDefaults = {
  variant: 'outlined',
  density: 'compact',
}

const tooltipDefaults = {
  openDelay: 100,
  location: 'bottom',
  maxWidth: 240,
}

export const statusColors = {
  green: '#006611',
  grey: '#1A1A1A',
  blue: '#005CE5',
  indigo: '#002B99',
  orange: '#AD5800',
  red: '#C20A20',
  pink: '#910818',
  yellow: '#80680A',
  lightgrey: '#666666',
} as const

const vuetify = createVuetify({
  locale: {
    adapter: createMappedVueI18nAdapter(),
  },
  date: {
    // adapter: DateFnsAdapter,
  },
  components: {
    VStepperVertical,
    VMaskInput,
    VFileUpload,
    VVideo,
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },
  defaults: {
    VAppBar: {
      height: 48,
      color: 'primary',
      class: 'text-white overflow-hidden',
    },
    MainNav: {
      VBtn: {
        tile: true,
        flat: true,
        height: '100%',
        class: 'text-white text-capitalize text-body-medium px-2',
        variant: 'text',
        border: 'b-md opacity-100',
      },
    },
    SubNav: {
      VToolbar: {
        height: 48,
        dense: true,
        flat: true,
        color: 'grey-darkest',
        class: 'text-white d-none d-lg-flex px-2 position-relative',
      },
      VBtn: {
        tile: true,
        flat: true,
        height: '100%',
        class: 'text-white text-capitalize text-body-medium px-2',
        variant: 'text',
        border: 'b-md opacity-100',
      },
    },
    VSnackbar: {
      closable: true,
      color: 'primary',
    },
    VNavigationDrawer: {
      // Needs to be below 2000 which is the z-index of select field overlays
      // Why this drawer isn't within a <dialog> element pushing it to the CSS
      // toplayer with a new z-index stack in the year of our Horse 2026 is
      // beyond me.
      // Anyway the navbar has a z-index of 1028, so it also can't be lower
      // than that.
      style: 'z-index: 1500',
    },
    VBtn: {
      class: 'text-none font-weight-bold',
    },
    VDialog: {
      'role': 'dialog',
      'class': 'rounded-lg',
      'scrim': 'rgba(0, 0, 0, 0.5)',
      'aria-modal': 'true',
      'width': 550,
      'maxWidth': '90vw',
      'VCard': {
        VCardTitle: {
          tag: 'h1',
          class: 'text-headline-medium',
        },
      },
    },
    VToolbar: {
      style: 'top: 48px',
    },
    VBreadcrumbs: {
      class: 'mx-0 pa-0 text-body-medium',
    },
    VBreadcrumbsItem: {
      class: 'px-0',
    },
    VTabs: {
      showArrows: true,
      alignTabs: 'start',
      density: 'comfortable',
      sliderColor: 'brand-primary',
    },
    VTab: {
      minWidth: 'auto',
    },
    VChip: {
      size: 'small',
    },
    VVideo: {
      color: 'brand-primary',
    },
    VFileUpload: {
      density: 'compact',
      variant: 'compact',
      maxHeight: '1rem',
      border: 'none',
      tile: true,
      class: 'py-4 px-2 justify-start',
    },
    VDataTableServer: {
      itemsPerPageOptions: [5, 10, 20],
      headerProps: {
        class: 'text-no-wrap font-weight-bold',
      },
      hover: true,
    },
    VDataTable: {
      itemsPerPageOptions: [5, 10, 20],
      headerProps: {
        class: 'text-no-wrap font-weight-bold',
      },
      hover: true,
    },
    VSwitch: {
      ...inputDefaults,
      density: 'comfortable',
      color: 'brand-primary',
    },
    VCheckbox: {
      ...inputDefaults,
      color: 'brand-primary',
    },
    VSelect: inputDefaults,
    VTextField: inputDefaults,
    VNumberInput: inputDefaults,
    VAutocomplete: inputDefaults,
    VMaskInput: inputDefaults,
    VCombobox: inputDefaults,
    VTextarea: inputDefaults,
    VFileInput: inputDefaults,
    BRichTextarea: inputDefaults,
    VPhoneInput: inputDefaults,
    VField: inputDefaults,
    BIdentityNumberField: inputDefaults,
    BTooltipBtn: {
      VTooltip: tooltipDefaults,
    },
    BActionTooltip: {
      VTooltip: { ...tooltipDefaults, offset: 20 },
    },
    BStatusTooltip: {
      VTooltip: tooltipDefaults,
    },
    BClampedText: {
      VTooltip: { ...tooltipDefaults, maxWidth: 480 },
    },
    VDateInput: {
      ...inputDefaults,
      prependInnerIcon: mdiCalendarBlank,
      prependIcon: '',
      pickerProps: {
        firstDayOfWeek: 1,
        hideTitle: true,
        color: 'primary',
      },
    },
    VDatePicker: {
      firstDayOfWeek: 1,
      hideTitle: true,
      color: 'primary',
    },
    VRadioGroup: {
      color: 'brand-primary',
    },
  },
  theme: {
    defaultTheme: 'amili',
    variations: {
      colors: ['primary', 'secondary', ...Object.keys(statusColors)],
      lighten: 2,
      darken: 2,
    },
    themes: {
      amili: {
        dark: false,
        colors: {
          'background': '#FFFFFF',
          'on-background': '#0A0D0D',
          'surface': '#FFFFFF',
          'on-surface': '#0A0D0D',
          'primary': '#0A0D0D',
          'on-primary': '#FFFFFF',
          'secondary': '#999999',
          'on-secondary': '#0A0D0D',
          'success': statusColors.green,
          'on-success': '#ffffff',
          'warning': statusColors.orange,
          'on-warning': '#ffffff',
          'error': statusColors.red,
          'on-error': '#ffffff',
          'info': statusColors.blue,
          'on-info': '#ffffff',

          // legacy colors
          'grey-darkest': '#333333',
          'grey-dark': '#4D4D4D',
          'grey-light': '#EFEFEF',
          'bankid': '#183E4F',
          'brand-primary': '#00E2E2',
          'brand-secondary': '#E1FCFF',
          'secondary-light': '#FAFAFA',

          // Legacy color formats
          // 'white': '#FFFFFF',
          // // 'primary': '#0A0D0D',
          // // 'secondary': '#999999',
          // 'neutral': '#9D9E9E',
          // // 'success': '#4CAF50',
          // // 'error': '#FF5252',
          // // 'warning': '#FB8C00',
          // 'green-dark': '#006644',
          // 'green-light': '#E5F0EC',
          // 'blue-dark': '#0052CC',
          // 'blue-light': '#E5EDFA',
          // 'indigo-dark': '#002B99',
          // 'indigo-light': '#A9BDEF',
          // 'orange-dark': '#8A4600',
          // 'orange-light': '#FFE6CC',
          // 'red-dark': '#D10B23',
          // 'red-light': '#FEE9EC',
          // 'pink-dark': '#8C0D0D',
          // 'pink-light': '#EDABAB',
          // 'yellow-dark': '#974F0C',
          // 'yellow-light': '#FFF5CC',
          // 'white-dark': '#495057',
          // 'white-light': '#f8f9fa',
          'clear': 'rgba(0, 0, 0, 0)',

          // States and statuses
          ...statusColors,
        },
        variables: {
          'font-heading': '"IBM Plex Serif", serif',
          'font-body': '"DM Sans Variable", sans-serif',
          'activated-opacity': 0.08,
          'hovered-opacity': 0.04,
          'selected-opacity': 0.12,
        },
      },
    },
  },
})

const ruleAliases = {
  min: (minValue: number, err?: string) => (v: unknown) =>
    typeof v === 'number' && v < minValue
      ? (err ?? i18n.global.t('rules.value_must_be_higher_than', [minValue]))
      : true,
  max: (maxValue: number, err?: string) => (v: unknown) =>
    typeof v === 'number' && v > maxValue ? (err ?? i18n.global.t('rules.value_must_be_lower_than', [maxValue])) : true,
}

type VuetifyRuleAliases = Required<NonNullable<Parameters<typeof createRulesPlugin>[0]['aliases']>>

declare module 'vuetify' {
  export function useRules(): VuetifyRuleAliases & typeof ruleAliases
}

export const rulesPlugin = createRulesPlugin(
  {
    aliases: ruleAliases,
  },
  vuetify.locale
)

export default vuetify
